"use client";

import { c as _c } from "react/compiler-runtime";
import { QueryKey } from "@/components/react-query-wrapper/ReactQueryWrapper";
import { WAVE_DROPS_PARAMS, getDefaultQueryRetry } from "@/components/react-query-wrapper/utils/query-utils";
import type { ApiDropsLeaderboardPage } from "@/generated/models/ApiDropsLeaderboardPage";
import { WaveDropsLeaderboardSort } from "@/hooks/useWaveDropsLeaderboard";
import { commonApiFetch } from "@/services/api/common-api";
import { useQueryClient } from "@tanstack/react-query";
import { debounce } from "lodash";
import React, { useCallback, useEffect, useMemo } from "react";
interface WaveleaderboardSortProps {
  readonly sort: WaveDropsLeaderboardSort;
  readonly onSortChange: (sort: WaveDropsLeaderboardSort) => void;
  readonly waveId?: string | undefined;
}
const SORT_DIRECTION_MAP: Record<WaveDropsLeaderboardSort, "ASC" | "DESC" | undefined> = {
  [WaveDropsLeaderboardSort.RANK]: undefined,
  [WaveDropsLeaderboardSort.RATING_PREDICTION]: "DESC",
  [WaveDropsLeaderboardSort.TREND]: "DESC",
  [WaveDropsLeaderboardSort.MY_REALTIME_VOTE]: undefined,
  [WaveDropsLeaderboardSort.CREATED_AT]: "DESC"
};
export const WaveleaderboardSort: React.FC<WaveleaderboardSortProps> = t0 => {
  const $ = _c(48);
  const {
    sort,
    onSortChange,
    waveId
  } = t0;
  const queryClient = useQueryClient();
  let t1;
  if ($[0] !== queryClient || $[1] !== sort || $[2] !== waveId) {
    t1 = targetSort => {
      if (!waveId || targetSort === sort) {
        return;
      }
      const sortDirection = SORT_DIRECTION_MAP[targetSort];
      const queryKey = [QueryKey.DROPS_LEADERBOARD, {
        waveId,
        page_size: WAVE_DROPS_PARAMS.limit,
        sort: targetSort,
        sort_direction: sortDirection
      }];
      queryClient.prefetchInfiniteQuery({
        queryKey,
        queryFn: async t2 => {
          const {
            pageParam
          } = t2;
          const params = {
            page_size: WAVE_DROPS_PARAMS.limit.toString(),
            sort: targetSort
          };
          if (sortDirection) {
            params.sort_direction = sortDirection;
          }
          if (typeof pageParam === "number") {
            params.page = `${pageParam}`;
          }
          return await commonApiFetch({
            endpoint: `waves/${waveId}/leaderboard`,
            params
          });
        },
        pages: 1,
        initialPageParam: null,
        getNextPageParam: lastPage => {
          if (targetSort === WaveDropsLeaderboardSort.MY_REALTIME_VOTE) {
            const haveZeroVotes = lastPage.drops.some(_temp);
            if (haveZeroVotes) {
              return null;
            }
          }
          return lastPage.next ? lastPage.page + 1 : null;
        },
        staleTime: 60000,
        ...getDefaultQueryRetry()
      }).catch(error => {
        console.warn("Failed to prefetch leaderboard data:", {
          waveId,
          targetSort,
          error: error instanceof Error ? error.message : error
        });
      });
    };
    $[0] = queryClient;
    $[1] = sort;
    $[2] = waveId;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  const prefetchSortImmediate = t1;
  let t2;
  if ($[4] !== prefetchSortImmediate) {
    t2 = debounce(prefetchSortImmediate, 300);
    $[4] = prefetchSortImmediate;
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  const prefetchSort = t2;
  let t3;
  let t4;
  if ($[6] !== prefetchSort) {
    t3 = () => () => prefetchSort.cancel();
    t4 = [prefetchSort];
    $[6] = prefetchSort;
    $[7] = t3;
    $[8] = t4;
  } else {
    t3 = $[7];
    t4 = $[8];
  }
  useEffect(t3, t4);
  let t5;
  if ($[9] !== sort) {
    t5 = buttonSort => {
      if (sort === buttonSort) {
        return "tw-px-4 tw-py-1.5 tw-text-xs tw-font-medium tw-border-0 tw-rounded-md tw-transition-colors tw-bg-white/10 tw-text-white tw-shadow-sm";
      }
      return "tw-px-4 tw-py-1.5 tw-text-xs tw-font-medium tw-border-0 tw-rounded-md tw-transition-colors tw-bg-transparent tw-text-iron-500 desktop-hover:hover:tw-text-iron-300";
    };
    $[9] = sort;
    $[10] = t5;
  } else {
    t5 = $[10];
  }
  const getButtonClassName = t5;
  const t6 = getButtonClassName(WaveDropsLeaderboardSort.RANK);
  let t7;
  if ($[11] !== onSortChange) {
    t7 = () => onSortChange(WaveDropsLeaderboardSort.RANK);
    $[11] = onSortChange;
    $[12] = t7;
  } else {
    t7 = $[12];
  }
  let t8;
  if ($[13] !== prefetchSort) {
    t8 = () => prefetchSort(WaveDropsLeaderboardSort.RANK);
    $[13] = prefetchSort;
    $[14] = t8;
  } else {
    t8 = $[14];
  }
  let t9;
  if ($[15] !== t6 || $[16] !== t7 || $[17] !== t8) {
    t9 = <button className={t6} onClick={t7} onMouseEnter={t8}>Current Vote</button>;
    $[15] = t6;
    $[16] = t7;
    $[17] = t8;
    $[18] = t9;
  } else {
    t9 = $[18];
  }
  const t10 = getButtonClassName(WaveDropsLeaderboardSort.RATING_PREDICTION);
  let t11;
  if ($[19] !== onSortChange) {
    t11 = () => onSortChange(WaveDropsLeaderboardSort.RATING_PREDICTION);
    $[19] = onSortChange;
    $[20] = t11;
  } else {
    t11 = $[20];
  }
  let t12;
  if ($[21] !== prefetchSort) {
    t12 = () => prefetchSort(WaveDropsLeaderboardSort.RATING_PREDICTION);
    $[21] = prefetchSort;
    $[22] = t12;
  } else {
    t12 = $[22];
  }
  let t13;
  if ($[23] !== t10 || $[24] !== t11 || $[25] !== t12) {
    t13 = <button className={t10} onClick={t11} onMouseEnter={t12}>Projected Vote</button>;
    $[23] = t10;
    $[24] = t11;
    $[25] = t12;
    $[26] = t13;
  } else {
    t13 = $[26];
  }
  const t14 = getButtonClassName(WaveDropsLeaderboardSort.TREND);
  let t15;
  if ($[27] !== onSortChange) {
    t15 = () => onSortChange(WaveDropsLeaderboardSort.TREND);
    $[27] = onSortChange;
    $[28] = t15;
  } else {
    t15 = $[28];
  }
  let t16;
  if ($[29] !== prefetchSort) {
    t16 = () => prefetchSort(WaveDropsLeaderboardSort.TREND);
    $[29] = prefetchSort;
    $[30] = t16;
  } else {
    t16 = $[30];
  }
  let t17;
  if ($[31] !== t14 || $[32] !== t15 || $[33] !== t16) {
    t17 = <button className={t14} onClick={t15} onMouseEnter={t16}>Hot</button>;
    $[31] = t14;
    $[32] = t15;
    $[33] = t16;
    $[34] = t17;
  } else {
    t17 = $[34];
  }
  const t18 = getButtonClassName(WaveDropsLeaderboardSort.CREATED_AT);
  let t19;
  if ($[35] !== onSortChange) {
    t19 = () => onSortChange(WaveDropsLeaderboardSort.CREATED_AT);
    $[35] = onSortChange;
    $[36] = t19;
  } else {
    t19 = $[36];
  }
  let t20;
  if ($[37] !== prefetchSort) {
    t20 = () => prefetchSort(WaveDropsLeaderboardSort.CREATED_AT);
    $[37] = prefetchSort;
    $[38] = t20;
  } else {
    t20 = $[38];
  }
  let t21;
  if ($[39] !== t18 || $[40] !== t19 || $[41] !== t20) {
    t21 = <button className={t18} onClick={t19} onMouseEnter={t20}>Newest</button>;
    $[39] = t18;
    $[40] = t19;
    $[41] = t20;
    $[42] = t21;
  } else {
    t21 = $[42];
  }
  let t22;
  if ($[43] !== t13 || $[44] !== t17 || $[45] !== t21 || $[46] !== t9) {
    t22 = <div id="tabsId" className="tw-flex tw-rounded-lg tw-whitespace-nowrap tw-border tw-border-solid tw-border-white/10 tw-bg-iron-950 tw-p-1">{t9}{t13}{t17}{t21}</div>;
    $[43] = t13;
    $[44] = t17;
    $[45] = t21;
    $[46] = t9;
    $[47] = t22;
  } else {
    t22 = $[47];
  }
  return t22;
};
function _temp(drop) {
  return drop.context_profile_context?.rating === 0;
}